import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { usePermissions } from "@/contexts/PermissionContext";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import {
  Archive,
  BarChart3,
  BookOpen,
  CalendarDays,
  Copy,
  Download,
  Edit2,
  GraduationCap,
  Layers,
  MoreHorizontal,
  Plus,
  QrCode,
  Search,
  X,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { norm } from "@/components/RelationSelects";
import { tr, LESSON_TYPE_LABEL, fmtDateBR } from "@/lib/labels";
import { EmptyState, PageHeader } from "@/components/doxa/AdminSurface";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MODALITY_BADGE = { PRESENTIAL: "doxa-badge-neutral", ONLINE: "doxa-badge-success", HYBRID: "doxa-badge-warning" };
const CG_STATUS = { ACTIVE: "Ativa", INACTIVE: "Inativa", ARCHIVED: "Arquivada" };
const MOD_STATUS = { ACTIVE: "Ativo", INACTIVE: "Inativo", ARCHIVED: "Arquivado" };
const statusBadge = (status) => status === "ACTIVE" ? "doxa-badge-success" : status === "ARCHIVED" ? "doxa-badge-critical" : "doxa-badge-neutral";
const CG_STATUS_OPTIONS = Object.entries(CG_STATUS).map(([value, label]) => ({ value, label }));
const MOD_STATUS_OPTIONS = Object.entries(MOD_STATUS).map(([value, label]) => ({ value, label }));
const MODALITY_OPTIONS = [
  { value: "PRESENTIAL", label: "Presencial" },
  { value: "ONLINE", label: "Online" },
  { value: "HYBRID", label: "Híbrida" },
];
const INPUT_CLASS = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm text-[#1F1F1F] outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

function formatPeriod(group) {
  if (group.start_date && group.end_date) return `${fmtDateBR(group.start_date)} → ${fmtDateBR(group.end_date)}`;
  if (group.start_date) return `A partir de ${fmtDateBR(group.start_date)}`;
  if (group.end_date) return `Até ${fmtDateBR(group.end_date)}`;
  return "Período não definido";
}

export default function Classes() {
  const { can } = usePermissions();
  const canViewReports = can("view.reports");
  const [groups, setGroups] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [qrGroup, setQrGroup] = useState(null);
  const [overviewGroup, setOverviewGroup] = useState(null);

  const load = () => api.get("/class-groups", { params: { include_inactive: true } }).then(({ data }) => {
    setGroups(data);
    if (selected) {
      const fresh = data.find((group) => group.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  });

  useEffect(() => { void load(); }, []);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  const visible = groups.filter((group) => {
    const statusVisible = showArchived || group.status !== "ARCHIVED";
    const queryVisible = !q || norm([group.name, group.course_name].join(" ")).includes(norm(q));
    return statusVisible && queryVisible;
  });

  const setStatus = async (group, status) => {
    try {
      await api.post(`/class-groups/${group.id}/status`, { status });
      toast.success("Status da turma atualizado.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="classes-page">
      <PageHeader
        eyebrow="Estrutura acadêmica"
        title="Turmas e módulos"
        description="Organize períodos, modalidade, módulos, inscrição e acompanhamento de cada turma."
        meta={`${visible.length} de ${groups.length} turma(s) visível(is)`}
        action={(
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
            data-testid="new-class-btn"
          >
            <Plus className="h-4 w-4" /> Nova turma
          </button>
        )}
      />

      <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-3 sm:p-4" data-testid="classes-filters">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A8378]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por turma ou curso…"
              className="h-11 w-full rounded-xl border border-[#E0D9CD] bg-[#FFFEFC] pl-9 pr-9 text-sm outline-none transition placeholder:text-[#A59C90] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
              data-testid="classes-search"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#F75A1B]"
                aria-label="Limpar busca"
                data-testid="classes-search-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <label className="inline-flex h-11 shrink-0 cursor-pointer items-center gap-2.5 rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] px-3.5 text-sm font-medium text-[#6B655E] transition hover:border-[#D8CFC2] hover:text-[#1F1F1F]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 accent-[#F75A1B]"
              data-testid="show-archived-toggle"
            />
            <Archive className="h-4 w-4" /> Mostrar arquivadas
          </label>
        </div>
      </section>

      {visible.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {visible.map((group) => (
            <ClassCard
              key={group.id}
              group={group}
              selected={selected?.id === group.id}
              onModules={() => setSelected(group)}
              onEdit={() => setEditing(group)}
              onOverview={() => setOverviewGroup(group)}
              onQr={() => setQrGroup(group)}
              onStatusChange={(status) => setStatus(group, status)}
            />
          ))}
        </div>
      ) : (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white px-5 py-3">
          <EmptyState
            icon={Layers}
            title={groups.length === 0 ? "Nenhuma turma cadastrada" : "Nenhuma turma encontrada"}
            description={groups.length === 0 ? "Crie a primeira turma para começar a estruturar módulos e aulas." : "Ajuste a busca ou exiba as turmas arquivadas."}
          />
          {groups.length === 0 && (
            <div className="pb-5 text-center">
              <button type="button" onClick={() => setEditing("new")} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2" data-testid="empty-new-class-btn">
                <Plus className="h-4 w-4" /> Criar turma
              </button>
            </div>
          )}
        </section>
      )}

      {selected && <ModulesPanel group={selected} onClose={() => setSelected(null)} onChanged={load} />}
      {editing && <ClassModal turma={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
      {qrGroup && <RegistrationQRModal group={qrGroup} onClose={() => setQrGroup(null)} />}
      {overviewGroup && <ClassOverviewModal group={overviewGroup} canViewReports={canViewReports} onClose={() => setOverviewGroup(null)} />}
    </div>
  );
}

function ClassCard({ group, selected, onModules, onEdit, onOverview, onQr, onStatusChange }) {
  return (
    <article
      className={`rounded-[18px] border bg-white p-4 transition sm:p-5 ${selected ? "border-[#F75A1B]/50 ring-2 ring-[#F75A1B]/10" : "border-[#E0D9CD] hover:border-[#D8CFC2] hover:shadow-[0_10px_30px_rgba(68,53,35,0.05)]"}`}
      data-testid={`class-card-${group.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Turma</p>
          <h2 className="mt-1 break-words font-display text-lg font-bold text-[#1F1F1F] sm:text-xl">{group.name}</h2>
          {group.course_name && <p className="mt-0.5 text-xs font-medium text-[#6B655E]">{group.course_name}</p>}
          {group.description && <p className="mt-2 text-xs leading-relaxed text-[#8A8378]">{group.description}</p>}
        </div>
        <span className={`doxa-badge shrink-0 ${statusBadge(group.status)}`}>{CG_STATUS[group.status] || group.status}</span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#6B655E]">
        <span className={`doxa-badge ${MODALITY_BADGE[group.default_lesson_type] || "doxa-badge-neutral"}`} data-testid={`class-modality-${group.id}`}>
          {tr(LESSON_TYPE_LABEL, group.default_lesson_type || "PRESENTIAL")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#FAF7F1] px-2.5 py-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-[#8A8378]" /> {formatPeriod(group)}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#6B655E]" data-testid={`class-stats-${group.id}`}>
        <span className="inline-flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#F75A1B]" />
          <span><strong className="font-display text-base font-bold text-[#1F1F1F]">{group.module_count || 0}</strong> módulos</span>
        </span>
        <span className="hidden h-4 w-px bg-[#E7DED1] sm:block" aria-hidden="true" />
        <span className="inline-flex items-center gap-2">
          <GraduationCap className="h-4 w-4 text-[#F75A1B]" />
          <span><strong className="font-display text-base font-bold text-[#1F1F1F]">{group.student_count || 0}</strong> alunos</span>
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-2 border-t border-[#EBE5DB] pt-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onModules}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl bg-[#FFF0E9] px-3 text-xs font-semibold text-[#D94A12] transition hover:bg-[#FFE6D9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30"
          data-testid={`manage-modules-${group.id}`}
        >
          <BookOpen className="h-3.5 w-3.5" /> Gerenciar módulos
        </button>

        <div className="flex min-w-0 items-center gap-2">
          <DoxaSelect
            value={group.status}
            onValueChange={onStatusChange}
            options={CG_STATUS_OPTIONS}
            size="compact"
            className="min-w-[116px] flex-1 sm:flex-none"
            contentClassName="min-w-[150px]"
            align="end"
            testId={`class-status-${group.id}`}
            ariaLabel={`Status da turma ${group.name}`}
          />
          <ClassActionsMenu group={group} onEdit={onEdit} onOverview={onOverview} onQr={onQr} />
        </div>
      </div>
    </article>
  );
}

function ClassActionsMenu({ group, onEdit, onOverview, onQr }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#8A8378] transition hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações da turma ${group.name}`}
          data-testid={`class-actions-${group.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-56 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações da turma</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{group.name}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-class-${group.id}`}>
          <Edit2 className="h-4 w-4" /> Editar turma
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onOverview} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`overview-class-${group.id}`}>
          <BarChart3 className="h-4 w-4" /> Acompanhamento
        </DropdownMenuItem>
        {group.status === "ACTIVE" && (
          <>
            <DropdownMenuSeparator className="bg-[#EBE5DB]" />
            <DropdownMenuItem onSelect={onQr} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`qr-class-${group.id}`}>
              <QrCode className="h-4 w-4" /> Inscrição / QR
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ClassOverviewModal({ group, canViewReports, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    try {
      const response = await api.get(`/class-groups/${group.id}/overview`);
      setData(response.data);
    } catch {
      if (initial) setData(null);
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => { void load({ initial: true }); }, [group.id]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  const cards = data ? [
    { label: "Alunos", value: data.total_students, testid: "ov-students" },
    { label: "Ativos", value: data.active_students, testid: "ov-active" },
    { label: "Críticos", value: data.critical_students, testid: "ov-critical" },
    { label: "Aulas", value: data.lessons, testid: "ov-lessons" },
    { label: "Aulas encerradas", value: data.lessons_done, testid: "ov-lessons-done" },
    { label: "Presença média", value: data.avg_attendance == null ? "Sem dados" : `${data.avg_attendance}%`, testid: "ov-attendance" },
    { label: "Justificativas", value: data.justifications, testid: "ov-justifications" },
    { label: "Tarefas", value: data.tasks, testid: "ov-tasks" },
    { label: "Materiais", value: data.materials, testid: "ov-materials" },
    { label: "Inscrições pendentes", value: data.pending_registrations, testid: "ov-pending" },
  ] : [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="class-overview-modal">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Acompanhamento da turma</h2>
          <button type="button" onClick={onClose} data-testid="overview-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-sm text-[#6B655E]">{group.name}</p>
        {loading ? (
          <p className="py-8 text-center text-sm text-[#6B655E]">Carregando…</p>
        ) : data ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {cards.map((card) => (
              <div key={card.testid} className="rounded-xl border border-[#EBE5DB] bg-[#FAF7F1] p-3" data-testid={card.testid}>
                <p className="break-words font-display text-xl font-bold">{card.value}</p>
                <p className="text-xs leading-tight text-[#6B655E]">{card.label}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-[#C53030]">Não foi possível carregar os indicadores.</p>
        )}
        {canViewReports && (
          <div className="mt-5 border-t border-[#EBE5DB] pt-4">
            <Link to={`/classes/${group.id}/analytics`} onClick={onClose} className="doxa-btn-primary inline-flex w-full items-center justify-center gap-2 px-4 py-2.5" data-testid="overview-open-analytics">
              <BarChart3 className="h-4 w-4" /> Abrir análise completa
            </Link>
            <p className="mt-2 text-center text-[11px] text-[#6B655E]">Gráficos, tendências, mapa de presença e drilldowns.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function RegistrationQRModal({ group, onClose }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const canvasWrapRef = useRef(null);

  const findLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/registration-links");
      const active = data.find((item) => item.class_group_id === group.id && item.status === "ACTIVE");
      setLink(active || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void findLink(); }, [group.id]);

  const publicUrl = link ? `${window.location.origin}/register/${link.token}` : "";

  const generate = async () => {
    setCreating(true);
    try {
      await api.post("/registration-links", { title: `Inscrição — ${group.name}`, class_group_id: group.id });
      toast.success("Link de inscrição gerado!");
      await findLink();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copiado!");
  };

  const downloadQR = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `inscricao-${group.name.replace(/\s+/g, "-").toLowerCase()}.png`;
    anchor.click();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="registration-qr-modal">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Inscrição da turma</h2>
          <button type="button" onClick={onClose} data-testid="qr-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-left text-sm text-[#6B655E]">{group.name}</p>

        {loading ? (
          <p className="py-10 text-sm text-[#6B655E]">Carregando…</p>
        ) : link ? (
          <>
            <p className="mb-4 mt-3 text-sm text-[#6B655E]">Mostre este QR Code para a pessoa se inscrever nesta turma.</p>
            <div ref={canvasWrapRef} className="mx-auto inline-block rounded-2xl border border-[#E0D9CD] bg-white p-4" data-testid="registration-qr-canvas">
              <QRCodeCanvas value={publicUrl} size={240} fgColor="#111111" />
            </div>
            <p className="mt-4 break-all rounded-lg bg-[#F6EFE4] p-2 text-xs text-[#6B655E]" data-testid="registration-qr-url">{publicUrl}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F6EFE4] px-3 py-2 text-sm" data-testid="qr-copy-link"><Copy className="h-4 w-4" /> Copiar link</button>
              <button type="button" onClick={downloadQR} className="inline-flex items-center gap-1.5 rounded-xl bg-[#F6EFE4] px-3 py-2 text-sm" data-testid="qr-download"><Download className="h-4 w-4" /> Baixar QR</button>
              <button type="button" onClick={onClose} className="rounded-xl border border-[#E0D9CD] px-3 py-2 text-sm" data-testid="qr-close-btn">Fechar</button>
            </div>
          </>
        ) : (
          <div className="py-6">
            <p className="mb-4 text-sm text-[#6B655E]">Esta turma ainda não tem um link público de inscrição ativo.</p>
            <button type="button" onClick={generate} disabled={creating} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="qr-generate-link">
              <QrCode className="h-4 w-4" /> {creating ? "Gerando…" : "Gerar link de inscrição"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ClassModal({ turma, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: turma?.name || "",
    description: turma?.description || "",
    start_date: turma?.start_date || "",
    end_date: turma?.end_date || "",
    default_lesson_type: turma?.default_lesson_type || "PRESENTIAL",
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome da turma.");
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        default_lesson_type: form.default_lesson_type,
      };
      if (turma) await api.put(`/class-groups/${turma.id}`, payload);
      else await api.post("/class-groups", payload);
      toast.success(turma ? "Turma atualizada!" : "Turma criada!");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="w-full max-w-lg overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(e) => e.stopPropagation()} data-testid="class-modal">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><GraduationCap className="h-4 w-4" /></span>
            <div>
              <h2 className="font-display text-lg font-bold">{turma ? "Editar turma" : "Nova turma"}</h2>
              <p className="mt-1 text-xs text-[#6B655E]">{turma ? "Atualize os dados estruturais sem alterar o histórico da turma." : "Defina a base da turma; aulas podem ajustar modalidade individualmente depois."}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="space-y-4 px-5 py-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Nome da turma</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT_CLASS} data-testid="class-name-input" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Descrição</label>
              <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${INPUT_CLASS} resize-y`} data-testid="class-desc-input" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Modalidade padrão</label>
              <DoxaSelect
                value={form.default_lesson_type}
                onValueChange={(value) => set("default_lesson_type", value)}
                options={MODALITY_OPTIONS}
                className="h-11 w-full"
                testId="class-modality-select"
                ariaLabel="Modalidade padrão da turma"
              />
              <p className="mt-1.5 text-[11px] leading-relaxed text-[#8A8378]">Novas aulas começam com esta modalidade e podem ser alteradas individualmente.</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Início</label>
                <input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={INPUT_CLASS} data-testid="class-start-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Término</label>
                <input type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={INPUT_CLASS} data-testid="class-end-input" />
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-[#EBE5DB] bg-[#FAF7F1] px-5 py-4 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="h-10 rounded-xl border border-[#E0D9CD] bg-white px-4 text-sm font-semibold text-[#4A4640] hover:bg-[#F6F3EE]">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary h-10 px-4" data-testid="class-submit">
              {busy ? "Salvando…" : turma ? "Salvar alterações" : "Criar turma"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModulesPanel({ group, onClose, onChanged }) {
  const [modules, setModules] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [reordering, setReordering] = useState(false);
  const modulesRef = useRef([]);
  const persistedOrderRef = useRef([]);
  const reorderingRef = useRef(false);

  const applyModules = (data) => {
    modulesRef.current = data;
    persistedOrderRef.current = data.map((module) => module.id);
    setModules(data);
  };

  const load = () => api.get("/modules", { params: { class_group_id: group.id, include_inactive: true } }).then(({ data }) => applyModules(data));
  useEffect(() => { void load(); }, [group.id]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/modules", { class_group_id: group.id, name });
      setName("");
      toast.success("Módulo criado!");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async (id) => {
    try {
      await api.put(`/modules/${id}`, { name: editName });
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (module, status) => {
    try {
      await api.post(`/modules/${module.id}/status`, { status });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReorder = (ordered) => {
    modulesRef.current = ordered;
    setModules(ordered);
  };

  const persistOrder = async (ordered = modulesRef.current) => {
    if (reorderingRef.current) return;
    const orderedIds = ordered.map((module) => module.id);
    if (orderedIds.join("|") === persistedOrderRef.current.join("|")) return;

    reorderingRef.current = true;
    setReordering(true);
    try {
      await api.post("/modules/reorder", { ordered_ids: orderedIds });
      persistedOrderRef.current = orderedIds;
      toast.success("Ordem dos módulos atualizada.");
    } catch (err) {
      toast.error(formatApiError(err));
      await load();
    } finally {
      reorderingRef.current = false;
      setReordering(false);
    }
  };

  const moveByKeyboard = async (index, direction) => {
    if (reorderingRef.current) return;
    const reordered = [...modulesRef.current];
    const target = index + direction;
    if (target < 0 || target >= reordered.length) return;
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    modulesRef.current = reordered;
    setModules(reordered);
    await persistOrder(reordered);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#1F1712]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(45,31,20,.18)]" onClick={(e) => e.stopPropagation()} data-testid="modules-panel">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] px-5 py-5">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><Layers className="h-4 w-4" /></span>
            <div>
              <h2 className="font-display text-lg font-bold">Módulos</h2>
              <p className="mt-1 text-xs text-[#6B655E]">{group.name} · {modules.length} módulo(s)</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-[#8A8378] hover:bg-[#F6F3EE]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-92px)] overflow-y-auto px-5 py-5">
          <form onSubmit={create} className="mb-5 rounded-[14px] border border-[#EBE5DB] bg-[#FAF7F1] p-3 sm:p-4">
            <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Adicionar módulo</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome do módulo"
                className={`${INPUT_CLASS} flex-1`}
                data-testid="module-name-input"
              />
              <button className="doxa-btn-primary inline-flex h-10 items-center justify-center gap-2 px-4" data-testid="module-create-btn">
                <Plus className="h-4 w-4" /> Criar módulo
              </button>
            </div>
          </form>

          {modules.length > 0 ? (
            <Reorder.Group
              axis="y"
              values={modules}
              onReorder={handleReorder}
              className="m-0 list-none space-y-2.5 p-0"
              layoutScroll
            >
              {modules.map((module, index) => (
                <ModuleReorderRow
                  key={module.id}
                  module={module}
                  index={index}
                  editingId={editingId}
                  editName={editName}
                  setEditName={setEditName}
                  setEditingId={setEditingId}
                  onSave={save}
                  onStatusChange={setStatus}
                  onDragEnd={() => void persistOrder(modulesRef.current)}
                  onKeyboardMove={(direction) => void moveByKeyboard(index, direction)}
                  reordering={reordering}
                />
              ))}
            </Reorder.Group>
          ) : (
            <EmptyState icon={BookOpen} title="Nenhum módulo nesta turma" description="Crie o primeiro módulo para organizar as aulas em uma sequência clara." compact />
          )}
        </div>
      </div>
    </div>
  );
}

function ModuleReorderRow({
  module,
  index,
  editingId,
  editName,
  setEditName,
  setEditingId,
  onSave,
  onStatusChange,
  onDragEnd,
  onKeyboardMove,
  reordering,
}) {
  const dragControls = useDragControls();
  const isEditing = editingId === module.id;
  const dragDisabled = reordering || isEditing;

  const handlePointerDown = (event) => {
    if (dragDisabled) return;
    dragControls.start(event);
  };

  const handleKeyDown = (event) => {
    if (dragDisabled) return;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onKeyboardMove(-1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      onKeyboardMove(1);
    }
  };

  return (
    <Reorder.Item
      value={module}
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={onDragEnd}
      whileDrag={{ scale: 1.012, boxShadow: "0 18px 46px rgba(68,53,35,0.16)", zIndex: 20 }}
      className="rounded-[14px] border border-[#EBE5DB] bg-[#FFFEFC] p-3.5 transition-colors hover:border-[#D8CFC2]"
      data-testid={`module-row-${module.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          disabled={dragDisabled}
          className="group grid h-10 w-10 shrink-0 cursor-grab place-items-center rounded-xl border border-[#EBE5DB] bg-white text-[#8A8378] transition hover:border-[#F75A1B]/35 hover:bg-[#FFF7F2] hover:text-[#F75A1B] active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/30 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ touchAction: "none" }}
          data-testid={`module-drag-${module.id}`}
          aria-label={`Reordenar módulo ${module.name}. Segure e arraste, ou use as setas para cima e para baixo.`}
          title="Segure e arraste para reordenar"
        >
          <span className="flex flex-col items-center gap-[3px]" aria-hidden="true">
            <span className="h-[2px] w-4 rounded-full bg-current" />
            <span className="h-[2px] w-4 rounded-full bg-current" />
            <span className="h-[2px] w-4 rounded-full bg-current" />
          </span>
        </button>

        <div className="min-w-0 flex-1">
          {isEditing ? (
            <input value={editName} onChange={(e) => setEditName(e.target.value)} className={INPUT_CLASS} data-testid={`module-edit-input-${module.id}`} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#FFF0E9] text-xs font-bold text-[#D94A12]">{index + 1}</span>
                <p className="break-words text-sm font-semibold text-[#1F1F1F]">{module.name}</p>
                <span className={`doxa-badge ${statusBadge(module.status)}`}>{MOD_STATUS[module.status] || module.status}</span>
              </div>
              <p className="mt-1.5 pl-9 text-xs text-[#8A8378]">{module.lesson_count || 0} aula(s) vinculada(s)</p>
            </>
          )}
        </div>

        {isEditing ? (
          <div className="flex shrink-0 items-center gap-2 sm:self-end">
            <button type="button" onClick={() => setEditingId(null)} className="h-9 rounded-xl border border-[#E0D9CD] bg-white px-3 text-xs font-semibold text-[#6B655E]">Cancelar</button>
            <button type="button" onClick={() => onSave(module.id)} className="doxa-btn-primary h-9 px-3 text-xs" data-testid={`module-save-${module.id}`}>Salvar</button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => { setEditingId(module.id); setEditName(module.name); }}
              className="grid h-9 w-9 place-items-center rounded-lg border border-[#E0D9CD] bg-white text-[#6B655E] transition hover:border-[#F75A1B]/35 hover:bg-[#FFF0E9] hover:text-[#D94A12]"
              data-testid={`module-edit-${module.id}`}
              aria-label={`Editar ${module.name}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <DoxaSelect
              value={module.status}
              onValueChange={(status) => onStatusChange(module, status)}
              options={MOD_STATUS_OPTIONS}
              size="compact"
              className="min-w-[112px]"
              contentClassName="min-w-[145px]"
              align="end"
              testId={`module-status-${module.id}`}
              ariaLabel={`Status do módulo ${module.name}`}
            />
          </div>
        )}
      </div>
    </Reorder.Item>
  );
}
